import clsx from "clsx";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";

import { AssessmentComment } from "./AssessmentComment";

import { SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { ContentSidebarHeader } from "components/materials/sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "components/materials/sidebar/ContentSidebarMain";
import { OverlayContainer } from "components/materials/sidebar/OverlayContainer";
import { displayOutcomeName } from "components/outcomes/helpers";
import { t } from "i18n/i18n";
import { OutcomeChip } from "mds/components/OutcomeChip";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { AssessPointsMarkerIcon } from "mds/icons";
import {
  pageSelectors,
  selectAssessmentBySubmissionId,
  selectNestedPageOutcomes,
  selectOutcomeAssessmentByAssessmentId,
  selectOutcomeByPageOutcomeId,
  selectPageOutcomeRubric,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { compactMap } from "utils/collections";
import { scoreColor, scoreDescriptionBackgroundColor } from "utils/outcome";
import { useQueryParam } from "utils/routing";

interface AssessmentDisplayViewProps {
  pageId: string;
}

export const AssessmentDisplayView: FC<AssessmentDisplayViewProps> = ({ pageId }) => {
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["max_points"]);
  const isSmallScreen = !useIsMdOrLarger();
  const submissionId = useQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const assessment = useObjectSelector(
    (s) => selectAssessmentBySubmissionId(s, submissionId),
    ["id", "points", "comment"],
  );

  const pageOutcomes = useListSelector((s) => selectNestedPageOutcomes(s, pageId), ["id"]);

  const outcomeAssessments = useListSelector(
    (s) => selectOutcomeAssessmentByAssessmentId(s, assessment?.id),
    ["page_outcome_id", "score"],
  );

  const assessmentItems = compactMap(pageOutcomes, (pageOutcome) => {
    const outcomeAssessment = outcomeAssessments.find(
      (oa) => oa.page_outcome_id === pageOutcome.id,
    );
    if (!outcomeAssessment) {
      return undefined;
    }
    return {
      pageOutcomeId: pageOutcome.id,
      score: outcomeAssessment.score,
    };
  });

  return (
    <OverlayContainer className={twMerge("bg-white", isSmallScreen && "h-auto")}>
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <div className="flex items-center gap-2">
          <AssessPointsMarkerIcon className="text-green" />
          <h3 className="m-0">{t("glossary.assessment")}</h3>
        </div>
      </ContentSidebarHeader>

      <ContentSidebarMain
        bottomPadding={false}
        className={isSmallScreen ? "overflow-y-visible overscroll-y-auto" : undefined}
      >
        <div className="flex flex-col gap-6">
          {assessmentItems.length > 0 && (
            <ol className="flex flex-col gap-6">
              {assessmentItems.map((assessmentItem) => (
                <AssessmentItemView
                  key={assessmentItem.pageOutcomeId}
                  pageOutcomeId={assessmentItem.pageOutcomeId}
                  score={assessmentItem.score}
                />
              ))}
            </ol>
          )}

          {page?.max_points && page.max_points > 0 && (
            <div className="flex items-center gap-1 text-black-tint-20">
              <span className="font-semibold">{t("submission_assessment.total_points")}:</span>
              <span>
                {assessment?.points}/{page.max_points}
              </span>
            </div>
          )}

          {assessment?.comment && <AssessmentComment comment={assessment.comment} />}
        </div>
      </ContentSidebarMain>
    </OverlayContainer>
  );
};

interface AssessmentItemViewProps {
  pageOutcomeId: string;
  score: number;
}

export const AssessmentItemView: FC<AssessmentItemViewProps> = ({ pageOutcomeId, score }) => {
  // TODO: this is very similar to the OutcomeAssessment component used by AssessmentGradingForm.
  // We should consolidate these implementations. (JN)
  const outcome = useObjectSelector(
    (s) => selectOutcomeByPageOutcomeId(s, pageOutcomeId),
    ["title"],
  );
  // Rubric consumers need all levels/fields.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const rubric = useObjectSelector((s) => selectPageOutcomeRubric(s, pageOutcomeId));
  const rubricLevel = rubric.find((level) => level.score === score);
  if (!outcome || !rubricLevel) return null;

  return (
    <li className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <OutcomeChip title={displayOutcomeName(outcome)} />
        <RubricButtons pageOutcomeId={pageOutcomeId} score={score} />
      </div>
      <div
        className={clsx(
          "rounded-lg px-1 py-0.5 text-black-tint-20",
          scoreDescriptionBackgroundColor(score),
        )}
      >
        <div className="flex flex-row items-center">
          <div
            className={clsx(
              "m-1.5 flex size-4 items-center justify-center rounded text-xs text-white",
              scoreColor(score),
            )}
          >
            {score}
          </div>
          <div className="grow font-medium">{rubricLevel.name}</div>
        </div>
        <div className="ml-7 pb-2 pr-1 text-sm font-normal">{rubricLevel.description}</div>
      </div>
    </li>
  );
};

interface RubricButtonsProps {
  pageOutcomeId: string;
  score: number;
}

const RubricButtons: FC<RubricButtonsProps> = ({ pageOutcomeId, score }) => {
  // TODO: this is very similar to the RubricButtons component used by AssessmentGradingForm.
  // We should consolidate these implementations. (JN)
  // Rubric consumers need all levels/fields.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const rubric = useObjectSelector((s) => selectPageOutcomeRubric(s, pageOutcomeId));

  return (
    <div className="flex items-center rounded-md bg-black-tint-97 text-black-tint-86">
      {rubric.map((rubricLevel) => {
        const highlighted = rubricLevel.score === score;
        return (
          <div
            aria-hidden={!highlighted}
            className={clsx(
              "flex size-7 items-center justify-center rounded-lg",
              highlighted && scoreColor(rubricLevel.score),
              highlighted && "text-white",
            )}
            key={rubricLevel.score}
          >
            {rubricLevel.score}
          </div>
        );
      })}
    </div>
  );
};
